import { getAiActQuestionnaireSlug } from "../ai-governance/questionnaires/helpers/getAiActQuestionnaireSlug";

const STATUS_LABELS = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
    not_available: "Not Available",
    gap: "Documentation Gap",
    needs_attention: "Needs Attention"
};

const STATUS_TONES = {
    in_progress: "in-progress",
    completed: "completed",
    gap: "gap",
    needs_attention: "needs-attention"
};

const PROGRESS_COLOR_COMPLETED = "#16A34A";
const PROGRESS_COLOR_DEFAULT = "#6C757E";

export const getAssessmentCardStatusTone = (card) => {
    if (!card?.status) {
        return getAssessmentCardPercent(card) >= 100
            ? "completed"
            : "not-started";
    }

    return STATUS_TONES[card.status] || card.status;
};

export const getAssessmentCardProgressColor = (card) => {
    return getAssessmentCardStatusTone(card) === "completed"
        ? PROGRESS_COLOR_COMPLETED
        : PROGRESS_COLOR_DEFAULT;
};

/**
 * Frontend URL keys stay stable; the assessment-cards payload keys each row
 * by backend slug (`slug` or `key`).
 */
export const ASSESSMENT_TABS = [
    {
        key: "processing-activity",
        title: "Processing Activity",
        slugs: ["processing_activity"]
    },
    {
        key: "eu-ai-act",
        title: "EU AI Act",
        slugs: ["ai_risk_assessment", "ai_risk_assessment_standard"]
    },
    {
        key: "fria",
        title: "FRIA",
        slugs: ["fria_assessment"]
    },
    {
        key: "risk-assessment",
        title: "Risk Assessment",
        slugs: ["risk_assessment"]
    },
    {
        key: "dpia",
        title: "DPIA",
        slugs: ["data_protection_impact_assessment"]
    },
    {
        key: "ce-conformity",
        title: "CE Conformity",
        slugs: ["conformity_assessment"]
    }
];

const clampPercent = (value) => {
    const percent = Number(value);

    if (!Number.isFinite(percent)) {
        return 0;
    }

    return Math.max(0, Math.min(100, Math.round(percent)));
};

const humanizeStatus = (status) => {
    return String(status)
        .replace(/_/g, " ")
        .replace(/^./, (character) => character.toUpperCase());
};

/**
 * The cards endpoint has used both `slug`/`completionPercent` and
 * `key`/`percentage`. Normalize once so the tab strip reads the payload
 * instead of falling back to hardcoded titles and 0%.
 */
export const normalizeAssessmentCards = (payload) => {
    const items = payload?.items || payload?.data?.items || [];

    return items.map((item) => {
        const slug = item?.slug || item?.key || "";

        return {
            ...item,
            slug,
            label: item?.label,
            status: item?.status,
            completionPercent: clampPercent(
                item?.completionPercent ?? item?.percentage ?? item?.percent
            ),
            visible: item?.visible !== false,
            currentlyRequired: item?.currentlyRequired
        };
    });
};

export const getAssessmentTabSlugs = (assessment, aiSetupMode) => {
    if (assessment.key === "eu-ai-act") {
        const preferred = getAiActQuestionnaireSlug(aiSetupMode);

        return [preferred, ...assessment.slugs.filter((slug) => slug !== preferred)];
    }

    return assessment.slugs;
};

export const findAssessmentCard = (items, slugs) => {
    if (!items?.length) {
        return null;
    }

    const candidates = Array.isArray(slugs) ? slugs : [slugs];

    return (
        candidates
            .map((slug) => items.find((item) => item?.slug === slug))
            .find(Boolean) || null
    );
};

export const getAssessmentCardPercent = (card) => {
    return clampPercent(card?.completionPercent);
};

export const getAssessmentCardStatusLabel = (card) => {
    if (!card?.status) {
        return getAssessmentCardPercent(card) >= 100
            ? "Completed"
            : "Not Started";
    }

    return STATUS_LABELS[card.status] || humanizeStatus(card.status);
};
